import { server } from '../core/server/axios';
import { BACKEND_URL } from '@/core/config';

/**
 * Ревизия кассы — модуль «CashAudit».
 *
 * Процесс трёхуровневый, и порядок вызовов жёсткий:
 *   1. заявка   /CashAudit      — назначается графиком либо создаётся вручную (adhoc), затем accept/reject
 *   2. акт      /CashAuditAct   — создаётся по подтверждённой заявке, в этот момент сервер снимает
 *                                 учётные остатки ПО ВСЕМ КАССАМ отделения; дальше комиссия, пересчёт, finish
 *   3. акт излишков и недостач  — создаётся сервером на finish, если есть расхождения;
 *                                 причины проставляются по одной строке
 *
 * На ревизию приходится по акту на каждую кассу из audit.kassaIds. Внутри акта сервер кладёт
 * kassas[] со всеми кассами отделения — работать нужно только с той, что равна act.kassaId.
 * Суммы фронт не считает и не отправляет — только количества.
 */

export enum CashAuditStatus {
    /** Не видна на фронте: пока не ушло уведомление, проверка остаётся внезапной */
    Planned = 0,
    NotificationSent = 1,
    AwaitingDecision = 2,
    Confirmed = 3,
    Rejected = 4,
    AwaitingAct = 5,
    ActCreated = 6,
    HasDeviations = 7,
    Completed = 8,
    Overdue = 9,
}

/** Запасные названия: сервер присылает готовое statusName, это — на случай его отсутствия */
export const cashAuditStatusNames: Record<CashAuditStatus, string> = {
    [CashAuditStatus.Planned]: "Запланирована",
    [CashAuditStatus.NotificationSent]: "Уведомление отправлено",
    [CashAuditStatus.AwaitingDecision]: "Ожидает решения",
    [CashAuditStatus.Confirmed]: "Подтверждена",
    [CashAuditStatus.Rejected]: "Отклонена",
    [CashAuditStatus.AwaitingAct]: "Ожидает акт",
    [CashAuditStatus.ActCreated]: "Акт сформирован",
    [CashAuditStatus.HasDeviations]: "Есть отклонения",
    [CashAuditStatus.Completed]: "Завершена",
    [CashAuditStatus.Overdue]: "Просрочена",
};

/** Название статуса для показа: как прислал сервер, без подмен — «Акт сформирован» ещё не «Завершена» */
export const auditStatusName = (audit?: Pick<CashAuditModel, "status" | "statusName"> | null) => (
    audit?.statusName || (audit ? cashAuditStatusNames[audit.status] : "") || ""
);

/** Статус самого акта — отдельный от статуса ревизии */
export enum CashAuditActStatus {
    Draft = 0,
    Finished = 1,
    Cancelled = 2,
}

/** Акт можно создавать только по подтверждённой заявке */
export const canCreateAct = (status: CashAuditStatus) => (
    status === CashAuditStatus.Confirmed || status === CashAuditStatus.AwaitingAct
);

export enum DeviationType {
    None = 0,
    Surplus = 1,
    Shortage = 2,
}

export enum CommissionRole {
    Chairman = 0,
    Member = 1,
}

/** Валюта считается в деньгах, золото — в штуках слитков */
export enum AssetKind {
    Currency = 0,
    Gold = 1,
}

export interface CashAuditModel {
    id: number;
    depId: number;
    depName?: string;
    /** Кассы, по которым нужны акты: ревизия закрывается, когда по каждой есть завершённый акт */
    kassaIds?: number[];
    /** В списке ревизий приходит одиночная касса */
    kassaId?: number;
    kassaName?: string;
    /** null, пока не ушло первое уведомление: до этого дата ревизии скрыта намеренно */
    plannedDate: string | null;
    status: CashAuditStatus;
    statusName: string;
    firstNotifiedAt?: string | null;
    secondNotifiedAt?: string | null;
    decisionAt?: string | null;
    decisionBy?: string | null;
    assignedToLogin?: string | null;
    assignedToName?: string | null;
    rejectReason?: string | null;
    /** Идентификаторы уже созданных актов — по одному на кассу */
    actIds?: number[];
    actId?: number | null;
    /** false — внеплановая: в график не входит и на трёхмесячное покрытие не влияет */
    isScheduled: boolean;
    hasDeviation?: boolean;
    deviationReason?: string | null;
}

export interface CashAuditActDenomination {
    currencyCode: string;
    denomination: number;
    /** Общее количество купюр номинала — годные вместе с ветхими */
    quantity: number;
    /**
     * Сколько из quantity ветхих: годные = quantity − wornQuantity. Поля может не быть:
     * в актах, сохранённых до появления wornCounts, его нет
     */
    wornQuantity?: number;
    /** Считает сервер */
    amount: number;
    /** Номинал вне справочника, объявленный через customDenominations */
    isCustom: boolean;
}

export interface CashAuditActCurrency {
    currencyCode: string;
    /** Учётный остаток — снимок на момент создания акта */
    accountedAmount: number;
    factAmount: number;
    deviation: number;
    deviationType: DeviationType;
    /** Готовое к выводу название: «Недостача», «Излишек», «Нет расхождений» */
    deviationTypeName: string;
    comment: string | null;
    denominations: CashAuditActDenomination[];
}

export interface CashAuditActGold {
    currencyCode: string;
    weightGrams: number;
    /** Учёт золота в штуках слитков, не в деньгах */
    accountedQuantity: number;
    factQuantity: number;
    deviation: number;
    deviationType: DeviationType;
    deviationTypeName: string;
    comment: string | null;
}

export interface CashAuditActKassa {
    kassaId: number;
    kassaName: string;
    /** Готовый признак «касса пройдена» — для галочки в списке касс отделения */
    isCounted: boolean;
    currencies: CashAuditActCurrency[];
    gold: CashAuditActGold[];
}

export interface CashAuditActCommissionMember {
    employeeId: number;
    fullName: string;
    /** Снимок должности на момент акта */
    position: string;
    role: CommissionRole;
    roleName: string;
}

export interface CashAuditActModel {
    id: number;
    actNumber: string;
    auditId: number;
    depId: number;
    kassaId: number;
    /** Логин кассира, работавшего на кассе во время ревизии, — материально ответственное лицо */
    cashierLogin?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
    duration?: string | null;
    status: CashAuditActStatus;
    statusName: string;
    hasDeviation: boolean;
    deviationActId?: number | null;
    commission: CashAuditActCommissionMember[];
    /** Общий комментарий к акту, отдельная ручка PUT /comment */
    comment?: string | null;
    /** Сервер кладёт сюда все кассы отделения — нужна только та, что совпадает с kassaId акта */
    kassas: CashAuditActKassa[];
}

export const isActEditable = (act?: CashAuditActModel | null) => act?.status === CashAuditActStatus.Draft;

export interface DeviationActLine {
    /** Тот самый lineId, который уходит в PUT .../deviation-act/reason */
    id: number;
    kassaId: number;
    kassaName: string;
    assetKind: AssetKind;
    assetKindName: string;
    currencyCode: string;
    /** Заполнен только для золота */
    weightGrams: number | null;
    /** Для золота это количество слитков, а не деньги */
    accountedValue: number;
    factValue: number;
    deviation: number;
    deviationType: DeviationType;
    deviationTypeName: string;
    reason: string | null;
}

export interface DeviationActModel {
    id: number;
    actNumber: string;
    auditActId: number;
    auditActNumber: string;
    depId: number;
    kassaId: number;
    auditDate: string;
    commission: CashAuditActCommissionMember[];
    lines: DeviationActLine[];
    /** Те же kassas[] из акта — расшифровка по купюрам для печатной формы */
    recount?: CashAuditActKassa[];
}

/** Событие истории ревизии или её акта */
export interface AuditHistoryEvent {
    id: number;
    auditId: number | null;
    actId: number | null;
    eventType: number;
    eventTypeName: string;
    /** Логин и ФИО того, кто совершил действие */
    actor: string;
    actorName: string;
    /** Момент действия в UTC */
    occurredAt: string;
    /** JSON-строка с деталями: состав полей зависит от типа события */
    payload?: string | null;
}

/** Акт по одной кассе: их столько же, сколько касс в audit.kassaIds */
export interface CashAuditActEntry {
    kassaId: number;
    kassaName: string | null;
    act: CashAuditActModel | null;
    deviationAct: DeviationActModel | null;
}

/** Ответ GET /CashAudit/{id}/full — вся карточка ревизии одним запросом */
export interface CashAuditFullModel {
    audit: CashAuditModel;
    acts: CashAuditActEntry[];
    /** Адрес отделения ревизии — для печатной формы акта */
    address?: string | null;
}

/** Справочник допустимых номиналов: { "KZT": [1, 2, ... 20000] } и веса слитков */
export interface DenominationsReference {
    currencies: Record<string, number[]>;
    goldWeightsGrams: number[];
}

export interface CashAuditFilter {
    depId?: number | null;
    status?: CashAuditStatus | null;
    from?: string | null;
    to?: string | null;
    /** Нумерация с 1; pageSize до 200, по умолчанию 20 */
    page?: number;
    pageSize?: number;
}

export interface AdhocAuditRequest {
    depId: number;
    kassaId: number;
    /** Полный момент времени в UTC: сегодняшний = ревизия сразу подтверждена, прошедший — DATE_IN_PAST */
    plannedDate?: string;
    reason?: string;
}

export interface CommissionRequest {
    chairmanEmployeeId: number;
    /** Председателя в этот список включать нельзя — вернётся MEMBER_DUPLICATES_CHAIR */
    memberEmployeeIds: number[];
}

/**
 * Пересчёт наличных одной кассы: только количества, ключ номинала — строка
 * в инвариантном формате («1000.5»). Тело замещает пересчёт этой кассы целиком
 * и не трогает остальные, поэтому валюту нужно слать со всеми её номиналами.
 * Ноль — валидное значение, именно так фиксируется «пересчитано, пусто».
 */
export interface DenominationCountsRequest {
    kassaId: number;
    /** Общее количество по номиналу — годные вместе с ветхими */
    counts: Record<string, Record<string, number>>;
    /**
     * Ветхие купюры тем же ключом номинала: { "USD": { "1": 4 } }. Это часть counts, а не
     * добавка к ним: 10 купюр по 1 USD, из них 4 ветхих — counts 10, wornCounts 4.
     * Шлём тем же составом номиналов, что и counts, включая нули: тело замещает
     * пересчёт кассы целиком, и пропущенный номинал оставил бы в акте прошлое значение.
     */
    wornCounts?: Record<string, Record<string, number>>;
    /** Номиналы вне справочника нужно объявить явно, иначе придёт DENOMINATION_NOT_ALLOWED */
    customDenominations?: Record<string, number[]>;
}

export interface GoldCountRequest {
    kassaId: number;
    currencyCode: string;
    weightGrams: number;
    factQuantity: number;
    comment: string | null;
}

export interface CashAuditListResult {
    items: CashAuditModel[];
    total: number;
}

/** Ошибки модуля приходят как { success, code, error, message } — message написан для человека */
export const auditErrorText = (e: any, fallback: string) => (
    e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback
);

export const auditErrorCode = (e: any): string => e?.response?.data?.error || "";

export class AuditController {
    /** Список ревизий. Сервер фильтрует по одному отделению — остальное досеиваем на фронте */
    async getAudits(filter: CashAuditFilter): Promise<CashAuditListResult> {
        const res = await server.get('/CashAudit', {
            baseURL: BACKEND_URL,
            params: {
                depId: filter?.depId ?? undefined,
                status: filter?.status ?? undefined,
                from: filter?.from ?? undefined,
                to: filter?.to ?? undefined,
                page: filter?.page ?? undefined,
                pageSize: filter?.pageSize ?? undefined,
            },
        });

        return { items: res?.items || [], total: res?.total ?? (res?.items || []).length };
    }

    /**
     * Предстоящие ревизии текущего пользователя, по возрастанию даты.
     * В отличие от GET /CashAudit отдаёт и статус Planned с раскрытой plannedDate —
     * это назначения на самого пользователя, для них дата уже не секрет.
     * Отделения не фильтрует: досеивать по городу и тенанту нужно на фронте.
     */
    async getPlannedAudits(page = 1, pageSize = 200): Promise<CashAuditListResult> {
        const res = await server.get('/CashAudit/planned', {
            baseURL: BACKEND_URL,
            params: { page, pageSize },
        });

        return { items: res?.items || [], total: res?.total ?? (res?.items || []).length };
    }

    async getAudit(id: number): Promise<CashAuditModel> {
        return await server.get(`/CashAudit/${id}`, { baseURL: BACKEND_URL });
    }

    /** Заявка + акт + акт излишков одним ответом — для карточки ревизии */
    async getAuditFull(id: number): Promise<CashAuditFullModel> {
        return await server.get(`/CashAudit/${id}/full`, { baseURL: BACKEND_URL });
    }

    async getDenominations(): Promise<DenominationsReference> {
        return await server.get('/CashAudit/denominations', { baseURL: BACKEND_URL });
    }

    /** Внеплановая ревизия: на сегодня создаётся сразу подтверждённой */
    async createAdhocAudit(request: AdhocAuditRequest): Promise<CashAuditModel> {
        return await server.post('/CashAudit/adhoc', request, { baseURL: BACKEND_URL });
    }

    async planAudits(periodStart: string): Promise<unknown> {
        return await server.post(`/CashAudit/plan?periodStart=${periodStart}`, {}, { baseURL: BACKEND_URL });
    }

    /** Решение принимают старший кассир или управляющий этой точки и директор, остальным — 401 */
    async acceptAudit(id: number): Promise<CashAuditModel> {
        return await server.post(`/CashAudit/${id}/accept`, {}, { baseURL: BACKEND_URL });
    }

    /** Причина обязательна: пустая строка вернёт REJECT_REASON_REQUIRED */
    async rejectAudit(id: number, reason: string): Promise<CashAuditModel> {
        return await server.post(`/CashAudit/${id}/reject`, { reason }, { baseURL: BACKEND_URL });
    }

    /**
     * Акт заводится на одну кассу ревизии; отделение сервер берёт из заявки.
     * Повтор по той же кассе — 409 ACT_ALREADY_EXISTS.
     */
    async createAct(auditId: number, kassaId?: number): Promise<CashAuditActModel> {
        return await server.post('/CashAuditAct', { auditId, kassaId }, { baseURL: BACKEND_URL });
    }

    async getAct(id: number): Promise<CashAuditActModel> {
        return await server.get(`/CashAuditAct/${id}`, { baseURL: BACKEND_URL });
    }

    async saveCommission(actId: number, request: CommissionRequest): Promise<CashAuditActModel> {
        return await server.put(`/CashAuditAct/${actId}/commission`, request, { baseURL: BACKEND_URL });
    }

    /** Ответ — весь акт целиком, перезапрашивать форму не нужно */
    async saveDenominations(actId: number, request: DenominationCountsRequest): Promise<CashAuditActModel> {
        return await server.put(`/CashAuditAct/${actId}/denominations`, request, { baseURL: BACKEND_URL });
    }

    /** Массив строк по всем кассам сразу; позицию с учётным остатком нужно слать даже с нулём */
    async saveGold(actId: number, request: GoldCountRequest[]): Promise<CashAuditActModel> {
        return await server.put(`/CashAuditAct/${actId}/gold`, request, { baseURL: BACKEND_URL });
    }

    /** Общий комментарий к акту: пустая строка его очищает, принимается и после завершения */
    async saveActComment(actId: number, comment: string): Promise<CashAuditActModel> {
        return await server.put(`/CashAuditAct/${actId}/comment`, { comment }, { baseURL: BACKEND_URL });
    }

    /** Проверит комиссию и полноту пересчёта, при расхождениях сам создаст акт излишков */
    async finishAct(actId: number): Promise<CashAuditActModel> {
        return await server.post(`/CashAuditAct/${actId}/finish`, {}, { baseURL: BACKEND_URL });
    }

    /** 404 DEVIATION_ACT_NOT_FOUND, если расхождений не было — это норма, а не ошибка */
    async getDeviationAct(actId: number): Promise<DeviationActModel> {
        return await server.get(`/CashAuditAct/${actId}/deviation-act`, { baseURL: BACKEND_URL });
    }

    async saveDeviationReason(actId: number, lineId: number, reason: string): Promise<DeviationActModel> {
        return await server.put(`/CashAuditAct/${actId}/deviation-act/reason`, { lineId, reason }, { baseURL: BACKEND_URL });
    }

    /** История ревизии — вся цепочка одним списком, включая события её актов */
    async getAuditHistory(auditId: number): Promise<AuditHistoryEvent[]> {
        return await server.get(`/CashAudit/${auditId}/history`, { baseURL: BACKEND_URL });
    }

    /** История действий по одному акту */
    async getActHistory(actId: number): Promise<AuditHistoryEvent[]> {
        return await server.get(`/CashAuditAct/${actId}/history`, { baseURL: BACKEND_URL });
    }
}
